
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Download, Upload, RefreshCw, CheckCircle, Database, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { getTeachers } from '@/services/db/teachers';
import { getStudents } from '@/services/db/students';
import { getAdmins } from '@/services/db/admins';
import { enhancedDocumentStorage } from '@/services/storage/enhancedDocumentStorage';
import { useQuery } from '@tanstack/react-query';

// Import other backup services
import { createStudentBackup, restoreStudentBackup } from '@/services/backup/studentBackup';
import { createTeacherBackup, restoreTeacherBackup } from '@/services/backup/teacherBackup';
import { createAdminBackup, restoreAdminBackup } from '@/services/backup/adminBackup';
import { createPaymentBackup, restorePaymentBackup } from '@/services/backup/paymentBackup';
import { createCertificateBackup, restoreCertificateBackup } from '@/services/backup/certificateBackup';

interface SystemStats {
  students: number;
  teachers: number;
  admins: number;
  documents: number;
  totalItems: number;
  estimatedSize: string;
}

const ComprehensiveBackupPanel = () => {
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [backupCreated, setBackupCreated] = useState(false);
  const [systemStats, setSystemStats] = useState<SystemStats>({
    students: 0,
    teachers: 0,
    admins: 0,
    documents: 0,
    totalItems: 0,
    estimatedSize: '0 KB'
  });
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Query all data sources with real-time updates - using correct import
  const { data: teachers = [], isLoading: teachersLoading, refetch: refetchTeachers } = useQuery({
    queryKey: ['backup-teachers'],
    queryFn: getTeachers,
    refetchInterval: 30000
  });

  const { data: students = [], isLoading: studentsLoading, refetch: refetchStudents } = useQuery({
    queryKey: ['backup-students'],
    queryFn: getStudents,
    refetchInterval: 30000
  });

  const { data: admins = [], isLoading: adminsLoading, refetch: refetchAdmins } = useQuery({
    queryKey: ['backup-admins'],
    queryFn: getAdmins,
    refetchInterval: 30000
  });

  // Calculate system statistics with real-time data
  useEffect(() => {
    const calculateStats = async () => {
      try {
        console.log('🔄 Calculating comprehensive backup stats with real-time data...');
        
        const studentsCount = Array.isArray(students) ? students.length : 0;
        const teachersCount = Array.isArray(teachers) ? teachers.length : 0;
        const adminsCount = Array.isArray(admins) ? admins.length : 0;
        
        // Get documents count from enhanced storage
        const documentStats = enhancedDocumentStorage.getStorageStats();
        const documentsCount = documentStats.totalDocuments;
        
        const totalItems = studentsCount + teachersCount + adminsCount + documentsCount;
        
        // Calculate comprehensive storage size
        const studentsData = JSON.stringify(students);
        const teachersData = JSON.stringify(teachers);
        const adminsData = JSON.stringify(admins);
        const documentsSize = documentStats.totalSize;
        
        const totalStorage = studentsData.length + teachersData.length + 
                           adminsData.length + documentsSize;
        const estimatedSize = totalStorage > 1024 * 1024 ? 
          `${(totalStorage / (1024 * 1024)).toFixed(2)} MB` :
          `${(totalStorage / 1024).toFixed(2)} KB`;

        const stats = {
          students: studentsCount,
          teachers: teachersCount,
          admins: adminsCount,
          documents: documentsCount,
          totalItems,
          estimatedSize
        };

        console.log('📊 Real-time comprehensive backup stats:', stats);
        setSystemStats(stats);
      } catch (error) {
        console.error('Error calculating system stats:', error);
        setSystemStats({
          students: Array.isArray(students) ? students.length : 0,
          teachers: Array.isArray(teachers) ? teachers.length : 0,
          admins: Array.isArray(admins) ? admins.length : 0,
          documents: 0,
          totalItems: (Array.isArray(students) ? students.length : 0) + 
                     (Array.isArray(teachers) ? teachers.length : 0) + 
                     (Array.isArray(admins) ? admins.length : 0),
          estimatedSize: '0 KB'
        });
      }
    };

    calculateStats();
  }, [students, teachers, admins]);

  const handleCreateComprehensiveBackup = async () => {
    setIsCreatingBackup(true);
    setBackupCreated(false);
    
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      
      console.log('🚀 Creating comprehensive backup with document integration...');
      
      // Get all documents from enhanced storage
      const documents = await enhancedDocumentStorage.loadDocuments();
      console.log(`📄 Including ${documents.length} documents in comprehensive backup`);
      
      // Create comprehensive backup with documents
      const comprehensiveData = {
        type: 'comprehensive_backup_enhanced',
        version: '4.0',
        timestamp,
        metadata: {
          totalStudents: systemStats.students,
          totalTeachers: systemStats.teachers,
          totalAdmins: systemStats.admins,
          totalDocuments: systemStats.documents,
          totalItems: systemStats.totalItems,
          estimatedSize: systemStats.estimatedSize,
          createdAt: new Date().toISOString(),
          includesDocuments: true,
          includesStudentAssignments: true
        },
        data: {
          students: JSON.stringify(students),
          teachers: JSON.stringify(teachers),
          admins: JSON.stringify(admins),
          documents: JSON.stringify(documents), // Include all documents
          payments: localStorage.getItem('nalc_payments'),
          certificates: localStorage.getItem('nalc_certificates'),
          settings: localStorage.getItem('nalc_settings'),
          // Include document storage metadata
          documentStorage: {
            stats: enhancedDocumentStorage.getStorageStats(),
            timestamp: new Date().toISOString()
          }
        }
      };

      const blob = new Blob([JSON.stringify(comprehensiveData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nalc-comprehensive-backup-enhanced-${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Comprehensive backup created successfully! Includes ${documents.length} documents with student assignments.`);
      setBackupCreated(true);
      setTimeout(() => setBackupCreated(false), 3000);
    } catch (error) {
      console.error('Failed to create comprehensive backup:', error);
      toast.error('Failed to create comprehensive backup');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    setIsRestoringBackup(true);
    
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      if (!backupData.type || !backupData.type.includes('comprehensive_backup')) {
        throw new Error('Invalid comprehensive backup file');
      }
      
      console.log('🔄 Restoring comprehensive backup with document integration...');
      
      // Restore core data
      if (backupData.data.students) {
        const studentsData = typeof backupData.data.students === 'string' ? 
          JSON.parse(backupData.data.students) : backupData.data.students;
        localStorage.setItem('nalc_students', JSON.stringify(studentsData));
      }
      if (backupData.data.teachers) {
        const teachersData = typeof backupData.data.teachers === 'string' ? 
          JSON.parse(backupData.data.teachers) : backupData.data.teachers;
        localStorage.setItem('nalc_teachers', JSON.stringify(teachersData));
      }
      if (backupData.data.admins) {
        const adminsData = typeof backupData.data.admins === 'string' ? 
          JSON.parse(backupData.data.admins) : backupData.data.admins;
        localStorage.setItem('nalc_admins', JSON.stringify(adminsData));
      }
      
      // Restore documents with student assignments preserved
      if (backupData.data.documents) {
        const documentsData = typeof backupData.data.documents === 'string' ? 
          JSON.parse(backupData.data.documents) : backupData.data.documents;
        
        console.log(`📄 Restoring ${documentsData.length} documents with student assignments...`);
        
        // Clear existing documents
        await enhancedDocumentStorage.clearAllDocuments();
        
        // Restore documents through enhanced storage to maintain structure
        await enhancedDocumentStorage.saveDocuments(documentsData);
        
        console.log(`✅ Documents restored with student assignments preserved`);
      }
      
      // Restore other data
      if (backupData.data.payments) {
        localStorage.setItem('nalc_payments', backupData.data.payments);
      }
      if (backupData.data.certificates) {
        localStorage.setItem('nalc_certificates', backupData.data.certificates);
      }
      if (backupData.data.settings) {
        localStorage.setItem('nalc_settings', backupData.data.settings);
      }
      
      // Force refresh all queries
      refetchStudents();
      refetchTeachers();
      refetchAdmins();
      
      toast.success('Comprehensive backup restored successfully! All data including documents and student assignments have been restored. Refreshing...');
      
      // Refresh the page to reload all data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Failed to restore comprehensive backup:', error);
      toast.error('Failed to restore comprehensive backup');
    } finally {
      setIsRestoringBackup(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Manual refresh function
  const handleRefreshStats = async () => {
    console.log('🔄 Manual refresh of comprehensive backup stats...');
    refetchStudents();
    refetchTeachers();
    refetchAdmins();
    toast.success('Statistics refreshed');
  };

  const isLoading = studentsLoading || teachersLoading || adminsLoading;

  return (
    <div className="space-y-6">
      {/* System Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            System Overview (Real-Time)
            <Button
              onClick={handleRefreshStats}
              variant="outline"
              size="sm"
              className="ml-auto"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Current system data that will be included in comprehensive backups - Updates every 30 seconds
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {isLoading ? '...' : systemStats.students}
              </div>
              <div className="text-sm text-muted-foreground">Students</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {isLoading ? '...' : systemStats.teachers}
              </div>
              <div className="text-sm text-muted-foreground">Teachers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {isLoading ? '...' : systemStats.admins}
              </div>
              <div className="text-sm text-muted-foreground">Admins</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {isLoading ? '...' : systemStats.documents}
              </div>
              <div className="text-sm text-muted-foreground">Documents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {isLoading ? '...' : systemStats.totalItems}
              </div>
              <div className="text-sm text-muted-foreground">Total Items</div>
            </div>
          </div>
          <div className="text-center text-sm text-muted-foreground">
            Estimated Size: {systemStats.estimatedSize} | Includes student-document assignments
          </div>
        </CardContent>
      </Card>

      {/* Backup Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Create Enhanced Comprehensive Backup
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Create a complete backup of all system data including students, teachers, admins, payments, 
            certificates, settings, and ALL DOCUMENTS with student assignments preserved.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
              <Button
                className="w-full h-14 flex items-center justify-center space-x-2"
                onClick={handleCreateComprehensiveBackup}
                disabled={isCreatingBackup || isLoading}
                variant={backupCreated ? "secondary" : "default"}
              >
                {isCreatingBackup ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                    <span>Creating Enhanced Backup...</span>
                  </>
                ) : backupCreated ? (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span>Backup Created!</span>
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5 mr-2" />
                    <span>Create Complete Backup</span>
                  </>
                )}
              </Button>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
              <Button
                className="w-full h-14 flex items-center justify-center space-x-2"
                onClick={handleSelectFile}
                disabled={isRestoringBackup}
                variant="outline"
              >
                {isRestoringBackup ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                    <span>Restoring Enhanced Backup...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 mr-2" />
                    <span>Restore from File</span>
                  </>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".json"
                  className="hidden"
                />
              </Button>
            </motion.div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComprehensiveBackupPanel;
